<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const store = useStore();
const { t } = useI18n();

const config = useMapGetter('supabase/getConfig');
const uiFlags = useMapGetter('supabase/getUIFlags');

const url = ref('');
const serviceKey = ref('');
const tableName = ref('dividas_ativas');

const isSaving = computed(() => uiFlags.value.isUpdating);
const isTesting = computed(() => uiFlags.value.isTesting);
const isFetching = computed(() => uiFlags.value.isFetching);

const isConfigured = computed(() => Boolean(config.value));

onMounted(async () => {
  await store.dispatch('supabase/get');
  if (config.value) {
    url.value = config.value.url || '';
    tableName.value = config.value.table_name || 'dividas_ativas';
  }
});

const onSave = async () => {
  if (!url.value || !tableName.value) {
    useAlert(t('INTEGRATION_SETTINGS.SUPABASE.VALIDATION.REQUIRED'));
    return;
  }
  if (!isConfigured.value && !serviceKey.value) {
    useAlert(t('INTEGRATION_SETTINGS.SUPABASE.VALIDATION.KEY_REQUIRED'));
    return;
  }

  const payload = { url: url.value, table_name: tableName.value };
  if (serviceKey.value) payload.service_key = serviceKey.value;

  const saved = await store.dispatch('supabase/save', payload);
  if (saved) {
    serviceKey.value = '';
    useAlert(t('INTEGRATION_SETTINGS.SUPABASE.SAVED'));
  }
};

const onTest = async () => {
  const result = await store.dispatch('supabase/testConnection');
  if (result?.success) {
    useAlert(t('INTEGRATION_SETTINGS.SUPABASE.TEST_OK'));
  } else {
    useAlert(result?.error || t('INTEGRATION_SETTINGS.SUPABASE.TEST_FAIL'));
  }
};

const onDelete = async () => {
  if (!window.confirm(t('INTEGRATION_SETTINGS.SUPABASE.DELETE_CONFIRM')))
    return;
  await store.dispatch('supabase/delete');
  url.value = '';
  serviceKey.value = '';
  tableName.value = 'dividas_ativas';
};
</script>

<template>
  <SettingsLayout
    :is-loading="isFetching"
    :loading-message="t('INTEGRATION_SETTINGS.SUPABASE.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('INTEGRATION_SETTINGS.SUPABASE.TITLE')"
        :description="t('INTEGRATION_SETTINGS.SUPABASE.DESCRIPTION')"
      />
    </template>
    <template #body>
      <div class="max-w-2xl flex flex-col gap-5 mt-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('INTEGRATION_SETTINGS.SUPABASE.FORM.URL.LABEL') }}
          </label>
          <Input
            v-model="url"
            :placeholder="
              t('INTEGRATION_SETTINGS.SUPABASE.FORM.URL.PLACEHOLDER')
            "
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('INTEGRATION_SETTINGS.SUPABASE.FORM.SERVICE_KEY.LABEL') }}
          </label>
          <Input
            v-model="serviceKey"
            type="password"
            :placeholder="
              isConfigured
                ? t(
                    'INTEGRATION_SETTINGS.SUPABASE.FORM.SERVICE_KEY.PLACEHOLDER_SET'
                  )
                : t(
                    'INTEGRATION_SETTINGS.SUPABASE.FORM.SERVICE_KEY.PLACEHOLDER'
                  )
            "
          />
          <p class="text-xs text-n-slate-11">
            {{ t('INTEGRATION_SETTINGS.SUPABASE.FORM.SERVICE_KEY.HELP') }}
          </p>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('INTEGRATION_SETTINGS.SUPABASE.FORM.TABLE_NAME.LABEL') }}
          </label>
          <Input v-model="tableName" />
        </div>

        <div class="flex gap-2 mt-2">
          <Button
            :label="t('INTEGRATION_SETTINGS.SUPABASE.FORM.SAVE')"
            :is-loading="isSaving"
            :disabled="isSaving"
            @click="onSave"
          />
          <Button
            v-if="isConfigured"
            faded
            :label="t('INTEGRATION_SETTINGS.SUPABASE.FORM.TEST')"
            :is-loading="isTesting"
            :disabled="isTesting"
            @click="onTest"
          />
          <Button
            v-if="isConfigured"
            faded
            ruby
            :label="t('INTEGRATION_SETTINGS.SUPABASE.FORM.DELETE')"
            @click="onDelete"
          />
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
